import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Shield, Clock, AlertTriangle, Truck, GitCompareArrows, Building2, Trash2, Download } from "lucide-react";
import logger from "@/lib/logger";

const SECTORS = [
  "Health", "Digital Infrastructure", "Energy", "Finance", "Public Administration",
  "Space", "Transport", "Water", "Manufacturing", "Chemicals", "Food",
  "Postal", "Research", "Waste Management", "Other",
];

const STATUS_STYLES = {
  not_started: { bg: "#FEE2E2", color: "#DC2626", label: "Not started" },
  in_progress: { bg: "#FEF3C7", color: "#D97706", label: "In progress" },
  implemented: { bg: "#DBEAFE", color: "#0055FF", label: "Implemented" },
  verified:    { bg: "#D1FAE5", color: "#059669", label: "Verified" },
};

const CLASS_STYLES = {
  essential:    { bg: "#FEE2E2", color: "#DC2626", label: "Essential entity" },
  important:    { bg: "#FEF3C7", color: "#D97706", label: "Important entity" },
  out_of_scope: { bg: "#E2E8F0", color: "#475569", label: "Likely out of scope" },
};

function CountdownBadge({ dueIso, sentAt, label }) {
  if (sentAt) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200">
        ✓ {label} sent
      </span>
    );
  }
  const due = new Date(dueIso);
  const now = new Date();
  const diffMs = due - now;
  const overdue = diffMs < 0;
  const hours = Math.floor(Math.abs(diffMs) / 36e5);
  const bg = overdue ? "bg-rose-50 text-rose-700 border-rose-200"
                     : hours < 6 ? "bg-amber-50 text-amber-700 border-amber-200"
                     : "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono border ${bg}`}>
      <Clock className="w-3 h-3" /> {label}: {overdue ? `overdue ${hours}h` : `${hours}h left`}
    </span>
  );
}

export default function NIS2() {
  const { loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [tab, setTab] = useState("scope");

  // Scope
  const [scope, setScope] = useState(null);
  const [scopeForm, setScopeForm] = useState({
    employees: 0, annual_turnover_eur: 0, sector: "Health",
    provides_essential_service: false, notes: "",
  });

  // Measures
  const [measures, setMeasures] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiSuggesting, setAiSuggesting] = useState(false);

  // Incidents
  const [incidents, setIncidents] = useState([]);
  const [incidentForm, setIncidentForm] = useState({
    title: "", description: "", detected_at: new Date().toISOString().slice(0, 16),
    severity: "high", competent_authority: "",
  });

  // Suppliers
  const [suppliers, setSuppliers] = useState([]);
  const [supplierForm, setSupplierForm] = useState({
    name: "", criticality: "high", last_assessed: "", next_due: "",
    findings_summary: "", contact_email: "",
  });

  // Cross-map
  const [crossMap, setCrossMap] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    api.get("/devices").then(({ data }) => {
      setProjects(data || []);
      if (data && data[0]) setPid(data[0].project_id);
    }).catch((e) => logger.warn("devices:", e?.response?.status));
    api.get("/nis2/cross-map").then(({ data }) => setCrossMap(data))
      .catch((e) => logger.warn("cross-map:", e?.response?.status));
  }, [authLoading]);

  const loadAll = useCallback(() => {
    if (!pid) return;
    api.get(`/nis2/scope/${pid}`).then(({ data }) => {
      setScope(data);
      if (data && data.employees != null) {
        setScopeForm({
          employees: data.employees || 0,
          annual_turnover_eur: data.annual_turnover_eur || 0,
          sector: data.sector || "Health",
          provides_essential_service: !!data.provides_essential_service,
          notes: data.notes || "",
        });
      }
    }).catch(() => {});
    api.get(`/nis2/measures/${pid}`).then(({ data }) => setMeasures(data)).catch(() => {});
    api.get(`/nis2/incidents?project_id=${pid}`).then(({ data }) => setIncidents(data)).catch(() => {});
    api.get(`/nis2/suppliers?project_id=${pid}`).then(({ data }) => setSuppliers(data)).catch(() => {});
  }, [pid]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const saveScope = async () => {
    try {
      const { data } = await api.post("/nis2/scope", { project_id: pid, ...scopeForm });
      setScope(data);
      toast.success(`Classified as ${data.classification.replace("_", " ")}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateMeasure = async (measure_id, status, evidence_notes) => {
    try {
      await api.post("/nis2/measures", { project_id: pid, measure_id, status, evidence_notes });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const suggestEvidence = async () => {
    if (!pid) { toast.error("Select a project first"); return; }
    setAiSuggesting(true);
    setAiSuggestions(null);
    try {
      const { data } = await api.post(`/nis2/measures/${pid}/suggest`);
      setAiSuggestions(data);
      toast.success(`${(data.suggestions || []).length} suggestion(s) returned`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suggestion call failed");
    } finally { setAiSuggesting(false); }
  };

  const acceptEvidence = async (measure_id, evidence_notes, cited_ids) => {
    if (!measures) return;
    const m = measures.measures.find((x) => x.measure_id === measure_id);
    const status = (m && m.status !== "not_started") ? m.status : "in_progress";
    try {
      const { data } = await api.post("/nis2/measures", {
        project_id: pid, measure_id, status, evidence_notes,
        cited_ids: cited_ids || [],
      });
      setAiSuggestions((prev) => prev && {
        ...prev,
        suggestions: prev.suggestions.filter((x) => x.measure_id !== measure_id),
      });
      const linksMsg = data.links_created > 0
        ? `Evidence accepted + ${data.links_created} trace link(s) created`
        : `Evidence accepted for measure #${measure_id}`;
      toast.success(linksMsg);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Accept failed");
    }
  };

  const createIncident = async () => {
    if (!incidentForm.title || !incidentForm.description) {
      toast.error("Title and description required"); return;
    }
    try {
      await api.post("/nis2/incidents", {
        project_id: pid,
        ...incidentForm,
        detected_at: new Date(incidentForm.detected_at).toISOString(),
      });
      toast.success("Incident recorded — timers started");
      setIncidentForm({ ...incidentForm, title: "", description: "" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const transitionMilestone = async (incident_id, milestone) => {
    try {
      await api.post(`/nis2/incidents/${incident_id}/transition`, { milestone });
      toast.success(`${milestone.replace("_", " ")} recorded`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addSupplier = async () => {
    if (!supplierForm.name) { toast.error("Supplier name required"); return; }
    try {
      await api.post("/nis2/suppliers", { project_id: pid, ...supplierForm });
      toast.success("Supplier added");
      setSupplierForm({ ...supplierForm, name: "", findings_summary: "" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeSupplier = async (id) => {
    if (!window.confirm("Delete this supplier record?")) return;
    try {
      await api.delete(`/nis2/suppliers/${id}`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadEvidencePack = async () => {
    if (!pid) { toast.error("Select a project first"); return; }
    try {
      const resp = await api.get(`/nis2/evidence-pack/${pid}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `nis2_evidence_pack_${pid}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Evidence pack downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const classMeta = scope?.classification ? CLASS_STYLES[scope.classification] : null;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6">
          <div className="vq-overline mb-2 flex items-center gap-2"><Shield className="w-3 h-3" /> NIS2 Directive</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
            NIS2 Cybersecurity Compliance
          </h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            Assistive workflow for scope self-classification, Article 21 ten-measure evidence,
            Article 23 incident timers (24h / 72h / 1 month), and supplier cybersecurity assessments.
            Not legal advice — final scope determination is made by each EU Member State competent authority.
          </p>
        </div>

        <div className="mb-6 max-w-md">
          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Device project</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="nis2-project-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id} data-testid={`nis2-project-${p.project_id}`}>{p.device_name || p.project_id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="nis2-tabs">
            <TabsTrigger value="scope" data-testid="nis2-tab-scope">Scope</TabsTrigger>
            <TabsTrigger value="measures" data-testid="nis2-tab-measures">Article 21</TabsTrigger>
            <TabsTrigger value="incidents" data-testid="nis2-tab-incidents">Incidents</TabsTrigger>
            <TabsTrigger value="suppliers" data-testid="nis2-tab-suppliers">Suppliers</TabsTrigger>
            <TabsTrigger value="crossmap" data-testid="nis2-tab-crossmap">Cross-map</TabsTrigger>
          </TabsList>

          {/* SCOPE */}
          <TabsContent value="scope">
            <div className="vq-card p-6" data-testid="nis2-scope-panel">
              {classMeta && (
                <div className="mb-4 inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold"
                     style={{ backgroundColor: classMeta.bg, color: classMeta.color }}
                     data-testid="nis2-scope-badge">
                  <Shield className="w-4 h-4" /> {classMeta.label}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Employees</Label>
                  <Input type="number" value={scopeForm.employees}
                         onChange={(e) => setScopeForm({ ...scopeForm, employees: parseInt(e.target.value || "0", 10) })}
                         data-testid="nis2-scope-employees" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Annual turnover (EUR)</Label>
                  <Input type="number" value={scopeForm.annual_turnover_eur}
                         onChange={(e) => setScopeForm({ ...scopeForm, annual_turnover_eur: parseFloat(e.target.value || "0") })}
                         data-testid="nis2-scope-turnover" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Sector</Label>
                  <Select value={scopeForm.sector} onValueChange={(v) => setScopeForm({ ...scopeForm, sector: v })}>
                    <SelectTrigger data-testid="nis2-scope-sector"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 mt-6">
                  <input type="checkbox"
                         checked={scopeForm.provides_essential_service}
                         onChange={(e) => setScopeForm({ ...scopeForm, provides_essential_service: e.target.checked })}
                         data-testid="nis2-scope-essential-service"
                         id="nis2-essential-service" />
                  <Label htmlFor="nis2-essential-service" className="text-sm text-slate-700 cursor-pointer">
                    Provides essential service (designated by member state)
                  </Label>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Notes</Label>
                  <Textarea value={scopeForm.notes}
                            onChange={(e) => setScopeForm({ ...scopeForm, notes: e.target.value })}
                            placeholder="Justification for classification"
                            data-testid="nis2-scope-notes" />
                </div>
              </div>
              <div className="mt-4">
                <Button onClick={saveScope} disabled={!pid} data-testid="nis2-scope-save-btn">Save classification</Button>
              </div>
              {scope?.classification && (
                <div className="text-xs text-slate-500 mt-4" data-testid="nis2-scope-note">
                  {scope.notes || "Assistive classification only — verify with your national competent authority."}
                </div>
              )}
            </div>
          </TabsContent>

          {/* MEASURES */}
          <TabsContent value="measures">
            <div className="vq-card p-6" data-testid="nis2-measures-panel">
              {measures && (
                <>
                  <div className="flex items-center gap-4 mb-4 flex-wrap">
                    <div className="font-display font-extrabold text-3xl text-slate-900" data-testid="nis2-measures-coverage">
                      {measures.coverage_pct}%
                    </div>
                    <div className="text-sm text-slate-600">
                      {measures.counts.verified + measures.counts.implemented} of 10 measures at implemented+ status
                    </div>
                    <Button onClick={suggestEvidence}
                            disabled={aiSuggesting || !pid}
                            data-testid="nis2-suggest-evidence-btn"
                            className="ml-auto bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold">
                      {aiSuggesting ? "Thinking…" : "Suggest evidence (AI)"}
                    </Button>
                    <Button onClick={downloadEvidencePack}
                            disabled={!pid}
                            variant="outline"
                            data-testid="nis2-evidence-pack-btn"
                            className="border-slate-300 font-semibold">
                      <Download className="w-4 h-4 mr-1" /> Evidence Pack PDF
                    </Button>
                  </div>

                  {aiSuggestions && (
                    <div className="mb-6 border border-[#0055FF]/20 bg-[#0055FF]/5 rounded p-4"
                         data-testid="nis2-suggest-panel">
                      <div className="text-xs text-slate-600 mb-2">
                        Claude scanned your workspace ({aiSuggestions.catalog_size?.risks || 0} risks
                        · {aiSuggestions.catalog_size?.threats || 0} threats
                        · {aiSuggestions.catalog_size?.documents || 0} documents
                        · {aiSuggestions.catalog_size?.trainings || 0} training records)
                        and proposed candidate evidence for each measure. Assistive only — review each before accepting.
                      </div>
                      {(aiSuggestions.suggestions || []).length === 0 ? (
                        <div className="text-xs text-slate-500" data-testid="nis2-suggest-empty">
                          {aiSuggestions.note || "No suggestions returned."}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {aiSuggestions.suggestions.map((s) => (
                            <div key={s.measure_id}
                                 className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded text-sm"
                                 data-testid={`nis2-suggest-row-${s.measure_id}`}>
                              <div className="font-mono text-xs text-slate-500 pt-0.5 w-8">#{s.measure_id}</div>
                              <div className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold h-5 ${
                                s.confidence >= 0.7 ? "bg-emerald-50 text-emerald-700"
                                : s.confidence >= 0.4 ? "bg-amber-50 text-amber-700"
                                : "bg-slate-100 text-slate-500"
                              }`}>
                                {Math.round(s.confidence * 100)}%
                              </div>
                              <div className="flex-1 text-slate-700">{s.evidence_notes || <span className="italic text-slate-400">No evidence proposed</span>}</div>
                              {s.evidence_notes && (
                                <Button size="sm" variant="outline"
                                        onClick={() => acceptEvidence(s.measure_id, s.evidence_notes, s.cited_ids)}
                                        data-testid={`nis2-suggest-accept-${s.measure_id}`}
                                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 shrink-0">
                                  Accept{s.cited_ids?.length ? ` + ${s.cited_ids.length} link${s.cited_ids.length > 1 ? "s" : ""}` : ""}
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>Measure</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Evidence notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {measures.measures.map((m) => {
                        const style = STATUS_STYLES[m.status];
                        return (
                          <TableRow key={m.measure_id} data-testid={`nis2-measure-row-${m.measure_id}`}>
                            <TableCell className="font-mono text-xs text-slate-500">{m.measure_id}</TableCell>
                            <TableCell>
                              <div className="font-semibold text-slate-900 text-sm">{m.title}</div>
                              <div className="text-xs text-slate-500 mt-0.5">{m.article_ref}</div>
                            </TableCell>
                            <TableCell>
                              <Select value={m.status}
                                      onValueChange={(v) => updateMeasure(m.measure_id, v, m.evidence_notes)}>
                                <SelectTrigger className="h-8 w-36" data-testid={`nis2-measure-status-${m.measure_id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(STATUS_STYLES).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <div className="mt-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-mono"
                                   style={{ backgroundColor: style.bg, color: style.color }}
                                   data-testid={`nis2-measure-badge-${m.measure_id}`}>
                                {style.label}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Textarea rows={2}
                                        defaultValue={m.evidence_notes || ""}
                                        onBlur={(e) => e.target.value !== (m.evidence_notes || "") && updateMeasure(m.measure_id, m.status, e.target.value)}
                                        placeholder="Link to policy / SOP / risk / control"
                                        className="text-xs"
                                        data-testid={`nis2-measure-notes-${m.measure_id}`} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}
            </div>
          </TabsContent>

          {/* INCIDENTS */}
          <TabsContent value="incidents">
            <div className="vq-card p-6 mb-4" data-testid="nis2-incident-create-panel">
              <div className="font-display font-bold text-slate-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Record a cybersecurity incident
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Title</Label>
                  <Input value={incidentForm.title}
                         onChange={(e) => setIncidentForm({ ...incidentForm, title: e.target.value })}
                         data-testid="nis2-incident-title" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Detected at</Label>
                  <Input type="datetime-local"
                         value={incidentForm.detected_at}
                         onChange={(e) => setIncidentForm({ ...incidentForm, detected_at: e.target.value })}
                         data-testid="nis2-incident-detected" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Description</Label>
                  <Textarea value={incidentForm.description}
                            onChange={(e) => setIncidentForm({ ...incidentForm, description: e.target.value })}
                            data-testid="nis2-incident-description" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Severity</Label>
                  <Select value={incidentForm.severity} onValueChange={(v) => setIncidentForm({ ...incidentForm, severity: v })}>
                    <SelectTrigger data-testid="nis2-incident-severity"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Competent authority</Label>
                  <Input value={incidentForm.competent_authority}
                         onChange={(e) => setIncidentForm({ ...incidentForm, competent_authority: e.target.value })}
                         placeholder="e.g. Ireland NCSC / BSI Germany / ANSSI France"
                         data-testid="nis2-incident-authority" />
                </div>
              </div>
              <div className="mt-4">
                <Button onClick={createIncident} disabled={!pid} data-testid="nis2-incident-create-btn">Record incident + start timers</Button>
              </div>
            </div>

            <div className="space-y-3" data-testid="nis2-incident-list">
              {incidents.length === 0 && (
                <div className="text-sm text-slate-500 p-4 border border-dashed border-slate-200 rounded" data-testid="nis2-incident-empty">
                  No incidents recorded for this project.
                </div>
              )}
              {incidents.map((i) => (
                <div key={i.incident_id} className="vq-card p-5" data-testid={`nis2-incident-row-${i.incident_id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="font-display font-bold text-slate-900">{i.title}</div>
                      <div className="text-xs text-slate-500 font-mono mt-1">
                        Awareness {new Date(i.awareness_at).toLocaleString()} • Severity {i.severity} • State {i.state}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <CountdownBadge dueIso={i.early_warning_due} sentAt={i.early_warning_sent_at} label="24h EW" />
                      <CountdownBadge dueIso={i.notification_due} sentAt={i.notification_sent_at} label="72h notice" />
                      <CountdownBadge dueIso={i.final_report_due} sentAt={i.final_report_submitted_at} label="1mo report" />
                    </div>
                  </div>
                  <div className="text-sm text-slate-700 mt-3">{i.description}</div>
                  <div className="mt-3 flex gap-2">
                    {!i.early_warning_sent_at && (
                      <Button size="sm" variant="outline"
                              onClick={() => transitionMilestone(i.incident_id, "early_warning")}
                              data-testid={`nis2-incident-ew-${i.incident_id}`}>Mark 24h EW sent</Button>
                    )}
                    {!i.notification_sent_at && (
                      <Button size="sm" variant="outline"
                              onClick={() => transitionMilestone(i.incident_id, "notification")}
                              data-testid={`nis2-incident-notif-${i.incident_id}`}>Mark 72h notice sent</Button>
                    )}
                    {!i.final_report_submitted_at && (
                      <Button size="sm" variant="outline"
                              onClick={() => transitionMilestone(i.incident_id, "final_report")}
                              data-testid={`nis2-incident-final-${i.incident_id}`}>Mark 1mo final report</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* SUPPLIERS */}
          <TabsContent value="suppliers">
            <div className="vq-card p-6 mb-4" data-testid="nis2-supplier-create-panel">
              <div className="font-display font-bold text-slate-900 mb-3 flex items-center gap-2">
                <Truck className="w-4 h-4" /> Add supplier cybersecurity assessment
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Supplier name</Label>
                  <Input value={supplierForm.name}
                         onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                         data-testid="nis2-supplier-name" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Criticality</Label>
                  <Select value={supplierForm.criticality} onValueChange={(v) => setSupplierForm({ ...supplierForm, criticality: v })}>
                    <SelectTrigger data-testid="nis2-supplier-criticality"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Last assessed</Label>
                  <Input type="date" value={supplierForm.last_assessed}
                         onChange={(e) => setSupplierForm({ ...supplierForm, last_assessed: e.target.value })}
                         data-testid="nis2-supplier-last" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Next due</Label>
                  <Input type="date" value={supplierForm.next_due}
                         onChange={(e) => setSupplierForm({ ...supplierForm, next_due: e.target.value })}
                         data-testid="nis2-supplier-next" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Findings summary</Label>
                  <Textarea value={supplierForm.findings_summary}
                            onChange={(e) => setSupplierForm({ ...supplierForm, findings_summary: e.target.value })}
                            placeholder="SOC 2 report, ISO 27001 cert, pentest results…"
                            data-testid="nis2-supplier-findings" />
                </div>
              </div>
              <div className="mt-4">
                <Button onClick={addSupplier} disabled={!pid} data-testid="nis2-supplier-add-btn">
                  <Building2 className="w-4 h-4 mr-1" /> Add supplier
                </Button>
              </div>
            </div>

            <div className="vq-card p-0 overflow-hidden" data-testid="nis2-supplier-list">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Criticality</TableHead>
                    <TableHead>Last assessed</TableHead>
                    <TableHead>Next due</TableHead>
                    <TableHead>Findings</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 py-8" data-testid="nis2-supplier-empty">
                        No suppliers recorded yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {suppliers.map((s) => (
                    <TableRow key={s.supplier_id} data-testid={`nis2-supplier-row-${s.supplier_id}`}>
                      <TableCell className="font-semibold text-slate-900">{s.name}</TableCell>
                      <TableCell>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-mono ${
                          s.criticality === "critical" ? "bg-rose-50 text-rose-700"
                          : s.criticality === "high" ? "bg-amber-50 text-amber-700"
                          : "bg-slate-100 text-slate-600"
                        }`}>{s.criticality}</span>
                      </TableCell>
                      <TableCell className="text-xs font-mono text-slate-500">{s.last_assessed || "—"}</TableCell>
                      <TableCell className="text-xs font-mono text-slate-500">{s.next_due || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-[280px] truncate">{s.findings_summary || ""}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => removeSupplier(s.supplier_id)}
                                data-testid={`nis2-supplier-delete-${s.supplier_id}`}
                                className="text-rose-600 hover:text-rose-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CROSS-MAP */}
          <TabsContent value="crossmap">
            <div className="vq-card p-6" data-testid="nis2-crossmap-panel">
              <div className="flex items-center gap-2 mb-3 text-slate-500">
                <GitCompareArrows className="w-4 h-4" />
                <div className="vq-overline text-[10px]">NIS2 ↔ MDR cyber ↔ ISO/IEC 27001 ↔ GDPR</div>
              </div>
              {crossMap && (
                <>
                  <p className="text-xs text-slate-500 mb-4">{crossMap.note}</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>NIS2</TableHead>
                        <TableHead>MDR / MDCG cybersecurity</TableHead>
                        <TableHead>ISO/IEC 27001:2022</TableHead>
                        <TableHead>GDPR</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {crossMap.rows.map((r, idx) => (
                        <TableRow key={r.nis2} data-testid={`nis2-crossmap-row-${idx}`}>
                          <TableCell className="text-xs text-slate-800">{r.nis2}</TableCell>
                          <TableCell className="text-xs text-slate-600">{r.mdr_cyber}</TableCell>
                          <TableCell className="text-xs text-slate-600">{r.iso27001}</TableCell>
                          <TableCell className="text-xs text-slate-600">{r.gdpr}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
